import React, { useEffect, useState } from 'react'
import { adminService, type CreateInstructorPayload } from '@/services/adminService'

export interface CreateInstructorModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  service?: typeof adminService
}

/**
 * CreateInstructorModal mints a new instructor account. Instructors
 * do not get permission grants — they are scoped through course
 * assignments (see InstructorSplit).
 */
export const CreateInstructorModal: React.FC<CreateInstructorModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  service = adminService,
}) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setName('')
    setEmail('')
    setPassword('')
    setPhone('')
    setError(null)
  }, [isOpen])

  const validate = (): string | null => {
    if (!name.trim()) return 'Nama wajib diisi'
    if (!email.trim() || !email.includes('@')) return 'Email tidak valid'
    if (password.length < 8) return 'Password minimal 8 karakter'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const msg = validate()
    if (msg) {
      setError(msg)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const payload: CreateInstructorPayload = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone: phone.trim() || undefined,
      }
      const res = await service.createInstructor(payload)
      if (!res.success) throw new Error(res.error?.message || 'Gagal menyimpan')
      onSuccess?.()
      onClose()
    } catch (err) {
      const e = err as Error
      setError(e.message || 'Gagal menyimpan')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="create-instructor-modal"
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Tambah Guru</h2>
          <button
            type="button"
            onClick={onClose}
            className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10"
            aria-label="tutup"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1" htmlFor="inst-name">
              Nama lengkap
            </label>
            <input
              id="inst-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="instructor-name-input"
              className="w-full px-3 py-2 bg-background-light dark:bg-background-dark border border-gray-200 dark:border-white/10 rounded text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
              autoComplete="new-password"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1" htmlFor="inst-email">
              Email
            </label>
            <input
              id="inst-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="instructor-email-input"
              className="w-full px-3 py-2 bg-background-light dark:bg-background-dark border border-gray-200 dark:border-white/10 rounded text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
              autoComplete="new-password"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1" htmlFor="inst-password">
              Password (minimal 8 karakter)
            </label>
            <input
              id="inst-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="instructor-password-input"
              className="w-full px-3 py-2 bg-background-light dark:bg-background-dark border border-gray-200 dark:border-white/10 rounded text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
              autoComplete="new-password"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1" htmlFor="inst-phone">
              Telepon (opsional)
            </label>
            <input
              id="inst-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="instructor-phone-input"
              className="w-full px-3 py-2 bg-background-light dark:bg-background-dark border border-gray-200 dark:border-white/10 rounded text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
            />
          </div>

          {error && (
            <div
              role="alert"
              data-testid="instructor-form-error"
              className="p-2 text-sm rounded bg-red-500/10 text-red-300"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10"
              data-testid="instructor-cancel"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-primary text-white font-semibold disabled:opacity-50"
              data-testid="instructor-submit"
            >
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateInstructorModal
